const ATTRIBUTION_COOKIE_NAME = "MIGIRI_ATTR";
const ATTRIBUTION_KEYS = ["gclid", "wbraid", "gbraid"] as const;
const ATTRIBUTION_QUERY_KEYS = [...ATTRIBUTION_KEYS] as const;
const ATTRIBUTION_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;
const VALID_IDENTIFIER_REGEX = /^[A-Za-z0-9_.-]+$/;

type AttributionKey = (typeof ATTRIBUTION_KEYS)[number];
export type AttributionCookie = {
  k: AttributionKey;
  v: string;
  t: number;
  utm_source?: string;
};

type QueryAttribution = Partial<Record<AttributionKey, string>>;

type MergeResult = {
  next: AttributionCookie | null;
  source: "query" | "existing" | "none";
};

type LocationLike = Pick<Location, "href" | "hostname" | "protocol" | "replace">;
type DocumentLike = Pick<Document, "cookie">;

type CaptureContext = {
  location: LocationLike;
  document: DocumentLike;
  now: () => number;
  logger?: Pick<Console, "info" | "warn">;
};

type ExtractedParams = {
  attribution: QueryAttribution;
  hadRelevantParam: boolean;
};

function isValidIdentifier(value: string | null): value is string {
  return Boolean(value && VALID_IDENTIFIER_REGEX.test(value));
}

export function extractAttributionParams(url: URL): ExtractedParams {
  const attribution: QueryAttribution = {};
  let hadRelevantParam = false;

  for (const key of ATTRIBUTION_QUERY_KEYS) {
    const value = url.searchParams.get(key);
    if (value !== null) {
      hadRelevantParam = true;
    }

    if (isValidIdentifier(value)) {
      attribution[key] = value;
    }
  }

  return { attribution, hadRelevantParam };
}

export function stripAttributionParams(url: URL): boolean {
  let removed = false;
  for (const key of ATTRIBUTION_QUERY_KEYS) {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      removed = true;
    }
  }
  return removed;
}

function readRawCookieValue(cookieString: string): string | null {
  const target = cookieString
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${ATTRIBUTION_COOKIE_NAME}=`));

  if (!target) {
    return null;
  }

  return target.slice(ATTRIBUTION_COOKIE_NAME.length + 1);
}

const UTM_SOURCE_MAX_LENGTH = 200;

function sanitizeUtmSource(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed || trimmed.length > UTM_SOURCE_MAX_LENGTH) {
    return null;
  }

  return trimmed;
}

/**
 * Tolerant utm_source lookup. The Framer site stores utm-only payloads
 * ({ t, utm_source }) with no click id, which parseAttributionCookie rejects
 * on purpose, so this reads the raw JSON instead.
 */
export function extractUtmSourceFromCookie(cookieString: string): string | null {
  const value = readRawCookieValue(cookieString);
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(decodeURIComponent(value)) as Record<string, unknown>;
    return sanitizeUtmSource(parsed?.utm_source);
  } catch {
    return null;
  }
}

export function parseAttributionCookie(cookieString: string): AttributionCookie | null {
  const value = readRawCookieValue(cookieString);
  if (value === null) {
    return null;
  }

  try {
    const decoded = decodeURIComponent(value);
    const parsed = JSON.parse(decoded) as AttributionCookie;

    if (
      typeof parsed?.k !== "string" ||
      typeof parsed?.v !== "string" ||
      typeof parsed?.t !== "number"
    ) {
      return null;
    }

    if (!ATTRIBUTION_KEYS.includes(parsed.k)) {
      return null;
    }

    if (!VALID_IDENTIFIER_REGEX.test(parsed.v)) {
      return null;
    }

    return parsed;
  } catch  {
    console.warn?.("MIGIRI_ATTR cookie parse failed");
    return null;
  }
}

export function resolveAttribution(
    existing: AttributionCookie | null,
    incoming: QueryAttribution,
    now: number
): MergeResult & { payload: AttributionCookie | null } {
    let source: MergeResult["source"] = "none";
    let selected: { k: AttributionKey; v: string } | null = null;

    // Is there any ID on the URL? last click win
    const pickedFromQueryKey = ATTRIBUTION_KEYS.find((k) => !!incoming[k]);
    if (pickedFromQueryKey) {
        selected = { k: pickedFromQueryKey, v: incoming[pickedFromQueryKey]! };
        source = "query";
    } else if (existing?.k && existing?.v) {
        // if not we use the existing one
        selected = { k: existing.k, v: existing.v };
        source = "existing";
    } else {
        return { payload: null, source: "none", next: null };
    }

    const payload: AttributionCookie = {
        k: selected.k,
        v: selected.v,
        t: now,
    };

    return { payload, source, next: payload };
}

export function determineCookieDomain(hostname: string): string {
  if (hostname === "migiri.app" || hostname.endsWith(".migiri.app")) {
    return ".migiri.app";
  }
  return hostname;
}

type UtmOnlyCookie = {
  t: number;
  utm_source: string;
};

function persistCookie(
  doc: DocumentLike,
  domain: string,
  payload: AttributionCookie | UtmOnlyCookie,
  secure: boolean
) {
  const encoded = encodeURIComponent(JSON.stringify(payload));
  const secureAttribute = secure ? ";Secure" : "";
  doc.cookie = `${ATTRIBUTION_COOKIE_NAME}=${encoded};Domain=${domain};Path=/;Max-Age=${ATTRIBUTION_MAX_AGE_SECONDS};SameSite=Lax${secureAttribute}`;
}

/**
 * Write a utm-only payload, mirroring the cookie the marketing site stores
 * when a campaign carries no Google click id. Used to keep influencer
 * attribution alive after the signup flow consumes (clears) the click-id
 * cookie.
 */
export function persistUtmSourceCookie(
  doc: DocumentLike,
  domain: string,
  utmSource: string,
  secure: boolean
) {
  persistCookie(doc, domain, { t: Date.now(), utm_source: utmSource }, secure);
}

export function captureAttribution(context: CaptureContext) {
  const { location, document, now, logger = console } = context;

  const url = new URL(location.href);
  const { attribution, hadRelevantParam } = extractAttributionParams(url);

  if (!hadRelevantParam) {
    return;
  }

  const existing = parseAttributionCookie(document.cookie);
  const { payload, source } = resolveAttribution(existing, attribution, now());

  if (!payload) {
    if (hadRelevantParam && stripAttributionParams(url)) {
      location.replace(url.toString());
    }
    return;
  }

  const domain = determineCookieDomain(location.hostname);
  const secure = location.protocol === "https:";
  // Rewriting the cookie must not drop the utm_source the marketing site
  // stored alongside the click id (influencer attribution, sent to the
  // subscription-status callable later).
  const utmSource = extractUtmSourceFromCookie(document.cookie);
  persistCookie(
    document,
    domain,
    utmSource ? { ...payload, utm_source: utmSource } : payload,
    secure
  );
  logger.info?.(`Attribution capture using ${payload.k} from ${source}`);

  if (stripAttributionParams(url)) {
    location.replace(url.toString());
  }
}

export function runAttributionCapture() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }

  try {
    captureAttribution({
      location: window.location,
      document: window.document,
      now: () => Date.now(),
      logger: console,
    });
  } catch (error) {
    console.warn("Attribution capture failed", error);
  }
}

export { ATTRIBUTION_COOKIE_NAME, ATTRIBUTION_KEYS };
